import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';

import { noop } from 'ui-components/default-props/noop';

import { emptyObject } from 'ui-components/default-props/empty';
import { ChildrenPropType } from '../../../../../prop-types/children';
import {
  DEFAULT_AUTO_FUND_AMOUNT,
  DEFAULT_CUTOFF,
  DEFAULT_PURCHASE_AMOUNT,
  DEFAULT_THRESHOLD,
} from '../../../payment-and-subscription/components/constants/add-funds-constants';
import useAddFunds from '../hooks/use-add-funds';
import useAutoFunds from '../hooks/use-auto-funds';

const AddFundsModalContext = React.createContext();
export const useAddFundsModalContext = () => useContext(AddFundsModalContext);

const AddFundsModalContextProvider = ({
  children,
  autoFundConfig = emptyObject,
  defaultPurchaseConfig = emptyObject,
  onAddFunds = noop,
  onSaveDefaultFundingAmount = noop,
  onSaveAutoFunding,
  onSuccess,
  submitting,
  setSubmitting,
  currencyCode,
}) => {
  const isAutoFundEnabled = !!autoFundConfig?.threshold?.value;
  const initialFundAmount = defaultPurchaseConfig?.amount;

  const [autoFundEnabled, setAutoFundEnabled] = useState(isAutoFundEnabled || false);
  const [balanceThreshold, setBalanceThreshold] = useState(autoFundConfig?.threshold?.value ?? DEFAULT_THRESHOLD);
  const [autoFundAmount, setAutoFundAmount] = useState(autoFundConfig?.amount?.value ?? DEFAULT_AUTO_FUND_AMOUNT);
  const [maxDailyFundings, setMaxDailyFundings] = useState(autoFundConfig?.cutoff ?? DEFAULT_CUTOFF);
  const [fundAmount, setFundAmount] = useState(initialFundAmount ?? DEFAULT_PURCHASE_AMOUNT);
  const [useAsDefault, setUseAsDefault] = useState(false);

  const { updateAutoFundsConfig, errors: autoFundErrors } = useAutoFunds({
    currencyCode,
    onSuccess,
    onSaveAutoFunding,
  });
  const { purchaseFunds, errors: addFundErrors } = useAddFunds({
    currencyCode,
    onSuccess,
    onAddFunds,
    onSaveDefaultFundingAmount,
  });

  const value = {
    submitting,
    setSubmitting,
    autoFundEnabled,
    setAutoFundEnabled,
    balanceThreshold,
    setBalanceThreshold,
    autoFundAmount,
    setAutoFundAmount,
    maxDailyFundings,
    setMaxDailyFundings,
    fundAmount,
    setFundAmount,
    useAsDefault,
    setUseAsDefault,
    updateAutoFundsConfig,
    autoFundErrors,
    purchaseFunds,
    addFundErrors,
    currencyCode,
  };

  return <AddFundsModalContext.Provider value={value}>{children}</AddFundsModalContext.Provider>;
};

AddFundsModalContextProvider.propTypes = {
  children: ChildrenPropType.isRequired,
  autoFundConfig: PropTypes.object,
  defaultPurchaseConfig: PropTypes.object,
  onAddFunds: PropTypes.func,
  onSaveDefaultFundingAmount: PropTypes.func,
  onSaveAutoFunding: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  setSubmitting: PropTypes.func.isRequired,
  currencyCode: PropTypes.string.isRequired,
};

export default AddFundsModalContextProvider;
